import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import SeoHead from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import { CheckCircle, SpinnerGap, XCircle } from "@phosphor-icons/react";

export default function PaymentSuccess() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = search.get("session_id");
  const [status, setStatus] = useState("pending");
  const [info, setInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;

    const poll = async (n = 0) => {
      if (cancelled) return;
      if (n > 12) {
        setStatus("timeout");
        return;
      }
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setStatus("success");
          return;
        }
        if (data.status === "expired") {
          setStatus("error");
          return;
        }
        setAttempts(n + 1);
        setTimeout(() => poll(n + 1), 2000);
      } catch (e) {
        setStatus("error");
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-50">
      <SeoHead title="Zahlung | Servus Autoglas" description="Zahlungsbestätigung" noindex />
      <Header minimal />
      <div className="max-w-xl mx-auto px-4 py-16 text-center">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10 space-y-5">
          {status === "pending" && (
            <>
              <SpinnerGap size={56} className="text-blue-600 mx-auto animate-spin" />
              <h1 className="text-2xl font-bold text-slate-900">Zahlung wird geprüft …</h1>
              <p className="text-slate-500">Versuch {attempts + 1} — bitte einen Moment Geduld.</p>
            </>
          )}
          {status === "success" && (
            <>
              <CheckCircle size={56} weight="fill" className="text-emerald-500 mx-auto" />
              <h1 className="text-2xl font-bold text-slate-900">Zahlung erfolgreich!</h1>
              <p className="text-slate-600">
                Betrag: <strong>{((info?.amount_total || 0) / 100).toFixed(2)} €</strong>
              </p>
              {info?.tint_booking && (
                <div className="rounded-2xl border-2 border-pink-200 bg-pink-50/50 p-5 text-left text-sm space-y-2" data-testid="payment-tint-details">
                  <p className="text-xs font-bold text-pink-600 uppercase tracking-widest">Ihre Scheibentönung</p>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Buchungsnummer</span><strong data-testid="payment-tint-booking-number">{info.tint_booking.booking_number}</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Fahrzeug</span><strong>{info.tint_booking.vehicle?.manufacturer} {info.tint_booking.vehicle?.model}{info.tint_booking.vehicle?.year ? ` (${info.tint_booking.vehicle.year})` : ""}</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Leistung</span><strong className="text-right">Tönung ab B-Säule<br />inkl. Heckscheibe</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Gesamtpreis</span><strong>{Number(info.tint_booking.price || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} € inkl. MwSt.</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Anzahlung (bezahlt)</span><strong className="text-emerald-600">{Number(info.tint_booking.deposit_amount || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Restbetrag vor Ort</span><strong>{(Number(info.tint_booking.price || 0) - Number(info.tint_booking.deposit_amount || 0)).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Termin</span><strong data-testid="payment-tint-date">{info.tint_booking.date && new Date(info.tint_booking.date + "T12:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Uhrzeit</span><strong>{info.tint_booking.time} Uhr</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Standort</span><strong className="text-right">Frankenthaler Str. 98,<br />67059 Ludwigshafen</strong></div>
                </div>
              )}
              {info?.sattlerei_booking && (
                <div className="rounded-2xl border-2 border-[#a9e3b6] bg-[#EAF8ED]/60 p-5 text-left text-sm space-y-2" data-testid="payment-sattlerei-details">
                  <p className="text-xs font-bold text-[#249143] uppercase tracking-widest">Ihre Sattlerei-Buchung</p>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Buchungsnummer</span><strong data-testid="payment-sattlerei-booking-number">{info.sattlerei_booking.booking_number}</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Leistung</span><strong className="text-right">{info.sattlerei_booking.service_label}</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Fahrzeug</span><strong>{info.sattlerei_booking.vehicle?.manufacturer} {info.sattlerei_booking.vehicle?.model}</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Gesamtpreis</span><strong>{Number(info.sattlerei_booking.price || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} € inkl. MwSt.</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Bezahlt</span><strong className="text-emerald-600">{Number(info.sattlerei_booking.paid_amount || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} € {info.sattlerei_booking.payment_mode === "full" ? "(Komplettzahlung)" : "(25 % Anzahlung)"}</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Restbetrag vor Ort</span><strong>{(Number(info.sattlerei_booking.price || 0) - Number(info.sattlerei_booking.paid_amount || 0)).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Fahrzeugabgabe</span><strong className="text-right">{info.sattlerei_booking.date && new Date(info.sattlerei_booking.date + "T12:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}<br />{info.sattlerei_booking.time} Uhr</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Vsl. Abholung</span><strong>{info.sattlerei_booking.pickup_date && new Date(info.sattlerei_booking.pickup_date + "T12:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Standort</span><strong className="text-right">Frankenthaler Str. 98,<br />67059 Ludwigshafen</strong></div>
                </div>
              )}
              <p className="text-sm text-slate-500">
                Ihr Termin ist gesichert. Wir senden eine Bestätigung per E-Mail.
              </p>
              <Button onClick={() => navigate("/")} className="bg-blue-600 hover:bg-blue-700 h-12 px-8" data-testid="payment-success-home">
                Zurück zur Startseite
              </Button>
            </>
          )}
          {(status === "error" || status === "timeout") && (
            <>
              <XCircle size={56} weight="fill" className="text-red-500 mx-auto" />
              <h1 className="text-2xl font-bold text-slate-900">{status === "timeout" ? "Status unklar" : "Zahlung fehlgeschlagen"}</h1>
              <p className="text-slate-600">
                {status === "timeout"
                  ? "Bitte prüfen Sie Ihre E-Mail oder kontaktieren Sie uns."
                  : "Die Zahlung wurde nicht abgeschlossen."}
              </p>
              <Button onClick={() => navigate("/")} variant="outline" className="h-12 px-8">
                Zurück zur Startseite
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
